import {
  useGetList,
  useListContext,
  useLocaleState,
  useTranslate,
} from "ra-core";
import { Link, useNavigate } from "react-router";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import type { Company, ProjectCompanyLink } from "../types";
import { countryName, roleKey } from "./projectUtils";

/** Ce que les fiches disent d'une société : ses rôles, les pays de ses projets, leur nombre. */
export const summariseLinks = (links: ProjectCompanyLink[]) => {
  const byCompany = new Map<
    string,
    {
      projects: Set<string>;
      roles: Map<string, number>;
      countries: Set<string>;
    }
  >();
  for (const link of links) {
    const key = String(link.company_id);
    const entry = byCompany.get(key) ?? {
      projects: new Set(),
      roles: new Map(),
      countries: new Set(),
    };
    entry.projects.add(String(link.project_id));
    entry.roles.set(link.role, (entry.roles.get(link.role) ?? 0) + 1);
    if (link.project_country) entry.countries.add(link.project_country);
    byCompany.set(key, entry);
  }
  return new Map(
    [...byCompany].map(([key, entry]) => [
      key,
      {
        projects: entry.projects.size,
        // Le rôle le plus fréquent d'abord.
        roles: [...entry.roles]
          .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
          .map(([role]) => role),
        countries: [...entry.countries].sort(),
      },
    ]),
  );
};

/** Liste des sociétés sur ordinateur : une ligne réglée par société, à la place de la grille de vignettes. */
export const CompanyTable = () => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const navigate = useNavigate();
  const { data } = useListContext<Company>();
  const ids = (data ?? []).map((company) => company.id);
  const { data: links, total } = useGetList<ProjectCompanyLink>(
    "project_company_links",
    {
      filter: { "company_id@in": `(${ids.join(",")})` },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "company_id", order: "ASC" },
    },
    { enabled: ids.length > 0 },
  );
  // Liens incomplets (plafond de la requête atteint) : rien plutôt qu'un compte faux.
  const complete = links != null && (total ?? 0) <= links.length;
  const summary = summariseLinks(complete ? links : []);
  const t = (key: string, options?: object) =>
    translate(`resources.projects.company_table.${key}`, options);
  if (!data?.length) return null;

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="text-xs text-muted-foreground">
          <tr className="border-b border-border">
            <th className="px-4 py-2.5 text-left font-semibold">{t("name")}</th>
            <th className="px-3 py-2.5 text-left font-semibold">
              {t("roles")}
            </th>
            <th className="px-3 py-2.5 text-left font-semibold">
              {t("countries")}
            </th>
            <th className="px-3 py-2.5 text-right font-semibold">
              {t("projects")}
            </th>
            <th className="px-4 py-2.5 text-right font-semibold">
              {t("contacts")}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.map((company) => {
            const facts = summary.get(String(company.id));
            const path = `/companies/${company.id}/show`;
            return (
              <tr
                key={company.id}
                onClick={() => navigate(path)}
                className="cursor-pointer transition-colors duration-(--motion-duration) hover:bg-muted/60"
              >
                <td className="px-4 py-2.5">
                  <Link
                    to={path}
                    onClick={(event) => event.stopPropagation()}
                    className="flex items-center gap-3 font-medium"
                  >
                    {/* Logos en gris : la couleur reste aux tampons et à ce qui est actif. */}
                    <span className="grayscale">
                      <CompanyAvatar record={company} width={20} height={20} />
                    </span>
                    <span className="line-clamp-2">{company.name}</span>
                  </Link>
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {facts?.roles
                    .slice(0, 2)
                    .map((role) => translate(roleKey(role), { _: role }))
                    .join(" · ")}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {facts &&
                    [
                      ...facts.countries
                        .slice(0, 3)
                        .map((code) => countryName(code, locale)),
                      ...(facts.countries.length > 3
                        ? [t("more", { count: facts.countries.length - 3 })]
                        : []),
                    ].join(", ")}
                </td>
                <td className="px-3 py-2.5 text-right font-semibold tabular-nums">
                  {facts?.projects ?? (complete ? 0 : "")}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                  {company.nb_contacts ?? 0}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
